import React from 'react';

// ============================================================
// LEAD FUNNEL
//
// 7-stage funnel for the Lead Performance page. Distinct from
// FunnelView (which is the per-stage / deal-value chart on the
// existing Team Dashboard). This one is lead-journey shaped:
//
//   Total leads → Replies → Qualified → Handoffs → Calls held
//                  → Proposals sent → Closed-won
//
// CRITICAL — mid-funnel stages (Replies, Handoffs, Calls, Proposals)
// come from daily_stats activity counts, not from lead-level stamps.
// The "unit" badge on each row marks which is which, and a caveat
// line above the chart names the cross-unit math. Lead-level
// (Total / Qualified / Closed-won) is exact.
//
// The two largest stage drop-offs are highlighted in red so the
// biggest leaks are visible at a glance.
// ============================================================

const ROW_COLORS = {
  normal: { bg: '#F9FAFB', border: '#E5E7EB', accent: '#9CA3AF', text: '#111827' },
  major:  { bg: '#FEF2F2', border: '#FECACA', accent: '#EF4444', text: '#991B1B' },
};

const UNIT_BADGE = {
  lead:     { bg: '#EFF6FF', border: '#BFDBFE', text: '#1D4ED8', label: 'lead' },
  activity: { bg: '#F3F4F6', border: '#E5E7EB', text: '#374151', label: 'activity' },
};

const Row = ({ idx, stage, total, isLast }) => {
  const palette = stage.is_major_drop ? ROW_COLORS.major : ROW_COLORS.normal;
  const unit = UNIT_BADGE[stage.unit] || UNIT_BADGE.lead;
  // Bar width proportional to count vs total. Avoid div-by-zero.
  const widthPct = total > 0 ? Math.max(3, Math.round((stage.count / total) * 100)) : 0;
  return (
    <>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '24px minmax(0, 1fr) auto',
        gap: 12,
        alignItems: 'center',
        padding: '10px 12px',
        borderRadius: 6,
        background: palette.bg,
        border: `1px solid ${palette.border}`,
        borderLeft: `4px solid ${palette.accent}`,
        marginBottom: 4,
      }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: '#9CA3AF' }}>{idx + 1}</div>
        <div style={{ minWidth: 0 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4,
            fontSize: 14, fontWeight: 500, color: palette.text,
          }}>
            <span style={{
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>{stage.label}</span>
            <span style={{
              display: 'inline-block',
              fontSize: 10, fontWeight: 500,
              padding: '1px 6px', borderRadius: 4,
              background: unit.bg, color: unit.text,
              border: `1px solid ${unit.border}`,
            }}>{unit.label}</span>
          </div>
          <div style={{
            height: 6, background: '#FFFFFF', borderRadius: 3, overflow: 'hidden',
            border: '1px solid #E5E7EB',
          }}>
            <div style={{
              width: `${widthPct}%`,
              height: '100%',
              background: stage.is_major_drop ? '#EF4444' : '#3B82F6',
            }} />
          </div>
        </div>
        <div style={{ textAlign: 'right', minWidth: 80 }}>
          <div style={{
            fontSize: 18, fontWeight: 600,
            color: stage.is_major_drop ? '#EF4444' : '#111827',
          }}>
            {stage.count.toLocaleString()}
          </div>
          {stage.conversion_from_prev !== null && (
            <div style={{ fontSize: 11, color: '#6B7280' }}>
              {stage.conversion_from_prev}% from {stage.conversion_from_label}
            </div>
          )}
        </div>
      </div>
      {!isLast && (
        <div style={{
          paddingLeft: 38, padding: '2px 0 2px 38px',
          fontSize: 11, color: '#9CA3AF',
        }}>↓</div>
      )}
    </>
  );
};

const LeadFunnel = ({ stages }) => {
  if (!stages || stages.length === 0) return null;
  const total = stages[0]?.count || 0;
  return (
    <div>
      <div style={{
        fontSize: 12, color: '#6B7280', marginBottom: 8,
        background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 6,
        padding: '8px 12px',
      }}>
        <strong style={{ color: '#92400E' }}>Cross-unit funnel:</strong>{' '}
        <span>
          stages tagged <span style={{ fontWeight: 600 }}>lead</span> are unique-lead counts;
          stages tagged <span style={{ fontWeight: 600 }}>activity</span> are aggregate
          counters from daily_stats (one prospect can yield several replies, calls, etc.).
          Conversions cross units so they over-state mid-funnel motion. Lead-level totals
          on either side (Total leads / Qualified / Closed-won) are exact.
        </span>
      </div>

      <div style={{ background: 'white', border: '1px solid #E5E7EB', borderRadius: 8, padding: 16 }}>
        {stages.map((stage, i) => (
          <Row
            key={stage.key}
            idx={i}
            stage={stage}
            total={total}
            isLast={i === stages.length - 1}
          />
        ))}
      </div>

      <div style={{
        display: 'flex', gap: 16, marginTop: 12,
        fontSize: 11, color: '#6B7280',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#EF4444' }} />
          biggest drop-offs
        </div>
      </div>
    </div>
  );
};

export default LeadFunnel;
